import Commons from '../../Services/Common/Common';
import Global from '../../Services/Common/Global';

let enginePromise = null;

/**
 * WASM 내부 텍스트 레이아웃 계산이 브라우저 Canvas 측정값을 콜백으로 요구한다.
 * 등록하지 않으면 줄바꿈/정렬 위치가 어긋나므로 init 이전에 반드시 설정해야 한다.
 */
const registerTextMeasurer = () => {
  if (window.measureTextWidth) return;

  let ctx = null;
  let lastFont = '';

  window.measureTextWidth = (font, text) => {
    if (!ctx) {
      ctx = document.createElement('canvas').getContext('2d');
    }
    if (font !== lastFont) {
      ctx.font = font;
      lastFont = font;
    }
    return ctx.measureText(text).width;
  };
};

/**
 * rhwp WASM 엔진을 1회만 초기화하고 모듈을 반환한다.
 * 8MB 수준의 바이너리라 동적 import로 초기 번들에서 분리한다.
 * @returns {Promise<{HwpDocument: Function}>}
 */
export const loadHwpEngine = () => {
  if (!enginePromise) {
    enginePromise = (async () => {
      registerTextMeasurer();

      const rhwp = await import('@rhwp/core');
      const wasmUrl = `${process.env.PUBLIC_URL || ''}/rhwp_bg.wasm`;
      await rhwp.default({ module_or_path: wasmUrl });

      return rhwp;
    })().catch((e) => {
      // 실패한 Promise를 캐시하면 재시도가 불가능해진다.
      enginePromise = null;
      throw e;
    });
  }

  return enginePromise;
};

/**
 * 화면마다 전체 URL / 상대 경로 / 앞에 슬래시가 붙은 경로가 뒤섞여 있어 상대 경로로 통일한다.
 * 파일서버가 UrlDecode를 수행하므로 인코딩 여부는 그대로 두어도 무방하다.
 * @param {string} path - 파일 경로 또는 전체 URL
 * @returns {string} 파일서버 기준 상대 경로
 */
export const toFileServerPath = (path) => {
  if (!path || typeof path !== 'string') return '';

  const marker = '/files/';
  const markerIndex = path.indexOf(marker);

  const relative =
    path.startsWith('http') && markerIndex >= 0
      ? path.slice(markerIndex + marker.length)
      : path;

  return relative.replace(/^\/+/, '');
};

/**
 * 파일서버에서 한글 파일 원본 바이너리를 내려받는다.
 * 정적 경로(/files)는 .hwp MIME이 등록돼 있지 않아 404가 나므로 다운로드 API를 경유한다.
 * @param {string} filePath - 파일 경로 또는 전체 URL
 * @param {string} fileName - 다운로드 응답용 파일명
 * @returns {Promise<Uint8Array>}
 */
export const fetchHwpBytes = async (filePath, fileName) => {
  const result = await Commons.Axios(
    'fileDown',
    Global.file.useUrl + Global.urls.Execute.UrlFileDownload,
    { FileName: fileName || 'preview.hwp', Url: toFileServerPath(filePath) }
  );

  const blob = result?.data;
  if (!blob || typeof blob.arrayBuffer !== 'function') {
    throw new Error('한글 파일을 내려받지 못했습니다.');
  }

  return new Uint8Array(await blob.arrayBuffer());
};
